import { useEffect, useState } from "react";
import {
  Ticket,
  Send,
  Pencil,
  Trash2,
  Plus,
  FolderOpen,
  FolderPlus,
  Check,
  Lock,
  Unlock,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import {
  getTickets,
  getTicketConfig,
  updateTicketConfig,
  getTicketPanel,
  sendTicketPanel,
  updateTicketPanel,
  deleteTicketPanel,
  getTicketOptions,
  createTicketOption,
  updateTicketOption,
  deleteTicketOption,
  getTicketCategories,
  createTicketCategory,
  renameTicketCategory,
  deleteTicketCategory,
  getTicketLogs,
  updateTicketLogs,
  getTicketOverview,
  ticketAction,
} from "@/api/tickets";
import { GlassCard } from "@/components/GlassCard";
import { Modal } from "./Modal";
import { showApiError, SkeletonRows, ErrorNote } from "./helpers";
import { Field, TextInput, NumInput, ColorInput, ChannelSelect, RoleSelect, ToggleRow, selectCls, Textarea } from "./fields";
import { ChannelSendCheck } from "./ChannelSendCheck";
import { validateUrl } from "./EmbedBuilder";

const TEXT_CHANNELS = ["text", "news"];
const actionBtn =
  "inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-paper-200 transition-colors duration-300 hover:bg-white hover:text-black disabled:opacity-40";

const OverviewStrip = ({ guildId }) => {
  const { data } = useApi(() => getTicketOverview(guildId));
  const o = data?.overview;
  if (!o) return null;
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="tickets-overview">
      {[["Total", o.total], ["Open", o.open], ["Closed", o.closed]].map(([label, value]) => (
        <div key={label} className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-center">
          <p className="font-display text-2xl font-semibold text-paper-100" data-testid={`tickets-overview-${label.toLowerCase()}`}>
            {value ?? 0}
          </p>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-paper-500">{label}</p>
        </div>
      ))}
    </div>
  );
};

const PanelSection = ({ guildId, channels }) => {
  const { data, loading, error, refetch } = useApi(() => getTicketPanel(guildId));
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ channel_id: "", title: "", description: "", color: "#FFFFFF", thumbnail: "", image: "", footer: "" });
  const [busy, setBusy] = useState(false);
  const panel = data?.panel;

  const send = async () => {
    if (!form.channel_id) {
      toast.error("Pick a channel to send the panel to.");
      return;
    }
    setBusy(true);
    try {
      const payload = { channel_id: form.channel_id };
      ["title", "description", "color", "thumbnail", "image", "footer"].forEach((k) => {
        if (form[k]) payload[k] = form[k];
      });
      const res = await sendTicketPanel(guildId, payload);
      if (res.interactive_view_attached === false) {
        toast.warning("Panel sent, but WITHOUT the dropdown menu — your Ticket cog doesn't expose a panel view factory. Use /ticketpanel in Discord for an interactive panel, or update the cog (I can show you how).");
      } else {
        toast.success(
          <span>
            Panel sent with its dropdown menu.{" "}
            {res.message_url && (
              <a href={res.message_url} target="_blank" rel="noopener noreferrer" className="underline">
                View in Discord
              </a>
            )}
          </span>
        );
      }
      setModal(false);
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const removePanel = async () => {
    try {
      await deleteTicketPanel(guildId);
      toast.success("Panel deleted from Discord and the database.");
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  const channelName = (id) => channels.find((c) => String(c.id) === String(id))?.name;

  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="tickets-panel-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Live panel</p>
        <div className="flex gap-2">
          <button onClick={() => setModal(true)} data-testid="tickets-panel-send-open" className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03]">
            <Send className="h-3 w-3" aria-hidden="true" />
            {panel ? "Re-send panel" : "Send panel"}
          </button>
          {panel && (
            <button onClick={removePanel} data-testid="tickets-panel-delete" className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-paper-200 transition-colors duration-300 hover:bg-white/10">
              Delete
            </button>
          )}
        </div>
      </div>
      {loading && <p className="mt-3 text-sm text-paper-500">Loading panel…</p>}
      {!loading && error && <p className="mt-3 text-sm text-paper-500">{error.message}</p>}
      {!loading && !error && !panel && (
        <p className="mt-3 text-sm text-paper-500">No panel sent yet — send one so members can open tickets.</p>
      )}
      {!loading && !error && panel && (
        <p className="mt-3 font-mono text-xs text-paper-300" data-testid="tickets-panel-info">
          “{panel.title}” in {channelName(panel.channel_id) ? `#${channelName(panel.channel_id)}` : panel.channel_id} · message {panel.message_id}
        </p>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Send ticket panel" testId="tickets-panel-modal">
        <div className="space-y-4">
          <Field label="Channel">
            <ChannelSelect value={form.channel_id} onChange={(v) => setForm((f) => ({ ...f, channel_id: v }))} channels={channels} types={TEXT_CHANNELS} testId="tickets-panel-channel" />
          </Field>
          <ChannelSendCheck guildId={guildId} channelId={form.channel_id} />
          <p className="text-xs text-paper-500">Leave fields empty to use the ticket configuration defaults.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title override">
              <TextInput value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} placeholder="Support Center" testId="tickets-panel-title" />
            </Field>
            <Field label="Color override">
              <ColorInput value={form.color} onChange={(v) => setForm((f) => ({ ...f, color: v }))} testId="tickets-panel-color" />
            </Field>
          </div>
          <Field label="Description override" hint="Line breaks are preserved in the embed.">
            <Textarea value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} placeholder={"Choose a category below…\nOur team will get back to you shortly."} testId="tickets-panel-description" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Thumbnail URL">
              <TextInput value={form.thumbnail} onChange={(v) => setForm((f) => ({ ...f, thumbnail: v }))} placeholder="https://…" testId="tickets-panel-thumbnail" />
            </Field>
            <Field label="Image URL">
              <TextInput value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} placeholder="https://…" testId="tickets-panel-image" />
            </Field>
          </div>
          <Field label="Footer override">
            <TextInput value={form.footer} onChange={(v) => setForm((f) => ({ ...f, footer: v }))} testId="tickets-panel-footer" />
          </Field>
          <button onClick={send} disabled={busy} data-testid="tickets-panel-send-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] disabled:opacity-50">
            <Send className="h-4 w-4" aria-hidden="true" />
            {busy ? "Sending…" : "Send panel to channel"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

const OptionModal = ({ guildId, option, categories, roles, onClose, onSaved }) => {
  const isEdit = !!option;
  const [form, setForm] = useState(
    option
      ? { name: option.name || "", emoji: option.emoji || "", description: option.description || "", channel_prefix: option.channel_prefix || "ticket", category_id: option.category_id || "", support_role_id: option.support_role_id || "" }
      : { name: "", emoji: "", description: "", channel_prefix: "ticket", category_id: "", support_role_id: "" }
  );
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Option name is required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        emoji: form.emoji.trim() || null,
        description: form.description.trim() || null,
        channel_prefix: form.channel_prefix.trim() || "ticket",
        category_id: form.category_id || null,
        support_role_id: form.support_role_id || null,
      };
      if (isEdit) {
        await updateTicketOption(guildId, option.id, payload);
        toast.success("Option updated.");
      } else {
        await createTicketOption(guildId, payload);
        toast.success("Option created.");
      }
      onClose();
      await onSaved();
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? `Edit "${option.name}"` : "New ticket option"} testId="ticket-option-modal">
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <TextInput value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="General Support" testId="ticket-option-name" />
          </Field>
          <Field label="Emoji">
            <TextInput value={form.emoji} onChange={(v) => setForm((f) => ({ ...f, emoji: v }))} placeholder="🎫" testId="ticket-option-emoji" />
          </Field>
        </div>
        <Field label="Description">
          <Textarea rows={2} value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} placeholder="Get help from the team" testId="ticket-option-description" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Channel prefix">
            <TextInput value={form.channel_prefix} onChange={(v) => setForm((f) => ({ ...f, channel_prefix: v }))} placeholder="ticket" testId="ticket-option-prefix" />
          </Field>
          <Field label="Category">
            <select value={form.category_id === null || form.category_id === undefined ? "" : String(form.category_id)} onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))} className={selectCls} data-testid="ticket-option-category">
              <option value="">None</option>
              {categories.map((cat) => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Support role">
            <RoleSelect value={form.support_role_id} onChange={(v) => setForm((f) => ({ ...f, support_role_id: v }))} roles={roles} testId="ticket-option-role" />
          </Field>
        </div>
        <button onClick={submit} disabled={busy} data-testid="ticket-option-save" className="w-full rounded-full bg-white py-3 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] disabled:opacity-50">
          {busy ? "Saving…" : isEdit ? "Save changes" : "Create option"}
        </button>
      </div>
    </Modal>
  );
};

const OptionsSection = ({ guildId, roles }) => {
  const optionsApi = useApi(() => getTicketOptions(guildId));
  const catsApi = useApi(() => getTicketCategories(guildId));
  const [modal, setModal] = useState(null); // null | 'new' | option
  const [newCat, setNewCat] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const options = optionsApi.data?.options || [];
  const categories = catsApi.data?.categories || [];
  const catName = (id) => categories.find((cat) => String(cat.id) === String(id))?.name;
  const roleName = (id) => roles.find((r) => String(r.id) === String(id))?.name;

  const removeOption = async (option) => {
    try {
      await deleteTicketOption(guildId, option.id);
      toast.success("Option deleted.");
      await optionsApi.refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await createTicketCategory(guildId, newCat.trim());
      toast.success("Category created in Discord.");
      setNewCat("");
      await catsApi.refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  const saveRename = async () => {
    if (!renameValue.trim()) return;
    try {
      await renameTicketCategory(guildId, renaming.id, renameValue.trim());
      toast.success("Category renamed.");
      setRenaming(null);
      await catsApi.refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  const removeCategory = async (cat) => {
    try {
      await deleteTicketCategory(guildId, cat.id);
      toast.success("Category deleted.");
      await catsApi.refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="tickets-options-section">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Panel options ({options.length})</p>
          <button onClick={() => setModal("new")} data-testid="tickets-option-add" className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03]">
            <Plus className="h-3 w-3" aria-hidden="true" />
            New option
          </button>
        </div>
        {optionsApi.loading && <p className="mt-3 text-sm text-paper-500">Loading options…</p>}
        {optionsApi.error && <p className="mt-3 text-sm text-paper-500">{optionsApi.error.message}</p>}
        <div className="mt-3 space-y-2">
          {!optionsApi.loading && options.length === 0 && <p className="text-xs text-paper-500">No options yet — the panel needs at least one.</p>}
          {options.map((option) => (
            <div key={option.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/5 px-3 py-2.5" data-testid={`ticket-option-row-${option.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-paper-100">
                  {option.emoji && <span className="mr-1.5">{option.emoji}</span>}
                  {option.name}
                </p>
                <p className="font-mono text-[10px] text-paper-500">
                  {option.channel_prefix}-…
                  {option.category_id && catName(option.category_id) ? ` · ${catName(option.category_id)}` : ""}
                  {option.support_role_id && roleName(option.support_role_id) ? ` · ${roleName(option.support_role_id)}` : ""}
                </p>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setModal(option)} aria-label="Edit option" data-testid={`ticket-option-edit-${option.id}`} className="rounded-full border border-white/10 p-2 text-paper-400 transition-colors hover:border-white/30 hover:text-paper-100">
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
                <button onClick={() => removeOption(option)} aria-label="Delete option" data-testid={`ticket-option-delete-${option.id}`} className="rounded-full border border-white/10 p-2 text-paper-500 transition-colors hover:border-white/40 hover:text-paper-100">
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="tickets-categories-section">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">
          <FolderOpen className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
          Ticket categories ({categories.length})
        </p>
        <div className="mt-3 flex gap-2">
          <TextInput value={newCat} onChange={setNewCat} placeholder="New category name" testId="tickets-category-input" />
          <button onClick={addCategory} disabled={!newCat.trim()} data-testid="tickets-category-add" className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03] disabled:opacity-40">
            <FolderPlus className="h-3.5 w-3.5" aria-hidden="true" />
            Create
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <span key={cat.id} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-xs text-paper-200" data-testid={`ticket-category-${cat.id}`}>
              {renaming?.id === cat.id ? (
                <>
                  <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="w-28 rounded-md border border-white/15 bg-ink-800 px-2 py-0.5 text-xs text-paper-100 focus:outline-none" data-testid="ticket-category-rename-input" />
                  <button onClick={saveRename} aria-label="Save rename" data-testid="ticket-category-rename-save" className="text-paper-100"><Check className="h-3 w-3" aria-hidden="true" /></button>
                </>
              ) : (
                <>
                  {cat.name}
                  <button onClick={() => { setRenaming(cat); setRenameValue(cat.name); }} aria-label={`Rename ${cat.name}`} data-testid={`ticket-category-rename-${cat.id}`} className="text-paper-500 transition-colors hover:text-paper-100">
                    <Pencil className="h-3 w-3" aria-hidden="true" />
                  </button>
                  <button onClick={() => removeCategory(cat)} aria-label={`Delete ${cat.name}`} data-testid={`ticket-category-delete-${cat.id}`} className="text-paper-500 transition-colors hover:text-paper-100">
                    <Trash2 className="h-3 w-3" aria-hidden="true" />
                  </button>
                </>
              )}
            </span>
          ))}
        </div>
      </div>

      {modal && (
        <OptionModal
          guildId={guildId}
          option={modal === "new" ? null : modal}
          categories={categories}
          roles={roles}
          onClose={() => setModal(null)}
          onSaved={optionsApi.refetch}
        />
      )}
    </div>
  );
};

const LogsSection = ({ guildId, channels }) => {
  const { data, loading, error, refetch } = useApi(() => getTicketLogs(guildId));
  const [value, setValue] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data !== null && !dirty) setValue(data?.logs?.channel_id || "");
  }, [data, dirty]);

  const save = async (v) => {
    try {
      await updateTicketLogs(guildId, v || null);
      toast.success(v ? "Ticket logs channel saved." : "Ticket logging disabled.");
      setDirty(false);
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  if (loading) return null;
  if (error) return <ErrorNote message={error.message} onRetry={refetch} testId="tickets-logs-error" />;

  return (
    <Field label="Ticket logs channel" hint="Transcripts and ticket events are posted here.">
      <div className="flex gap-2">
        <div className="flex-1">
          <ChannelSelect value={value} onChange={(v) => { setValue(v); setDirty(true); }} channels={channels} types={TEXT_CHANNELS} testId="tickets-logs-channel" />
        </div>
        <button onClick={() => save(value)} disabled={!dirty} data-testid="tickets-logs-save" className="shrink-0 rounded-full bg-white px-5 py-2 text-xs font-semibold text-black transition-transform duration-300 hover:scale-[1.03] disabled:opacity-40">
          Save
        </button>
      </div>
    </Field>
  );
};

const ticketDate = (v) => {
  if (v === null || v === undefined || v === "") return null;
  let d;
  if (typeof v === "number" || /^\d+(\.\d+)?$/.test(String(v))) {
    let n = Number(v);
    if (n < 1e12) n *= 1000; // Unix seconds → milliseconds
    d = new Date(n);
  } else {
    d = new Date(v);
  }
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

const TicketsList = ({ guildId, channels = [] }) => {
  const [status, setStatus] = useState("open");
  const { data, loading, error, refetch } = useApi(() => getTickets(guildId, status), { deps: [status] });
  const [busy, setBusy] = useState(null);
  const tickets = data?.tickets || [];
  const channelGone = (ticket) =>
    channels.length > 0 && !channels.some((c) => String(c.id) === String(ticket.channel_id));

  const act = async (ticket, action) => {
    setBusy(`${ticket.channel_id}-${action}`);
    try {
      await ticketAction(guildId, ticket.channel_id, action);
      toast.success(`Ticket ${action === "delete" ? "deleted" : `${action}ed`}.`);
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="tickets-list-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Tickets</p>
        <div className="flex gap-2" role="group" aria-label="Ticket status filter">
          {["open", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`tickets-filter-${s}`}
              aria-pressed={status === s}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                status === s ? "border-white bg-white text-black" : "border-white/15 text-paper-400 hover:text-paper-100"
              }`}
            >
              {s === "open" ? "Open" : "Closed"}
            </button>
          ))}
        </div>
      </div>
      {loading && <p className="mt-3 text-sm text-paper-500">Loading tickets…</p>}
      {error && <p className="mt-3 text-sm text-paper-500">{error.message}</p>}
      {!loading && !error && tickets.length === 0 && (
        <p className="mt-3 text-xs text-paper-500" data-testid="tickets-list-empty">No {status} tickets.</p>
      )}
      <div className="mt-3 space-y-2">
        {tickets.map((ticket) => {
          const gone = channelGone(ticket);
          const date = ticketDate(ticket.created_at);
          return (
          <div key={ticket.channel_id} className={`rounded-lg border border-white/5 px-3 py-2.5 ${gone ? "opacity-50" : ""}`} data-testid={`ticket-row-${ticket.channel_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-mono text-xs text-paper-100">
                  #{ticket.ticket_id} · channel {ticket.channel_id}
                  {gone && (
                    <span className="ml-2 rounded-full border border-white/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-paper-400" data-testid={`ticket-gone-${ticket.channel_id}`}>
                      Channel deleted
                    </span>
                  )}
                </p>
                <p className="font-mono text-[10px] text-paper-500">
                  owner {ticket.owner_id}
                  {ticket.claimed_by ? ` · claimed by ${ticket.claimed_by}` : " · unclaimed"}
                  {date ? ` · ${date}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {!gone && (
                  <a href={`https://discord.com/channels/${guildId}/${ticket.channel_id}`} target="_blank" rel="noopener noreferrer" className={actionBtn} data-testid={`ticket-open-${ticket.channel_id}`}>
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    Open
                  </a>
                )}
                {!gone && ticket.status === "open" && !ticket.claimed_by && (
                  <button onClick={() => act(ticket, "claim")} disabled={!!busy} className={actionBtn} data-testid={`ticket-claim-${ticket.channel_id}`}>Claim</button>
                )}
                {!gone && ticket.status === "open" && (
                  <button onClick={() => act(ticket, "close")} disabled={!!busy} className={actionBtn} data-testid={`ticket-close-${ticket.channel_id}`}>
                    <Lock className="h-3 w-3" aria-hidden="true" />
                    Close
                  </button>
                )}
                {!gone && ticket.status === "closed" && (
                  <button onClick={() => act(ticket, "reopen")} disabled={!!busy} className={actionBtn} data-testid={`ticket-reopen-${ticket.channel_id}`}>
                    <Unlock className="h-3 w-3" aria-hidden="true" />
                    Reopen
                  </button>
                )}
                <button onClick={() => act(ticket, "delete")} disabled={!!busy} className={actionBtn} data-testid={`ticket-delete-${ticket.channel_id}`}>
                  <Trash2 className="h-3 w-3" aria-hidden="true" />
                  Delete
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

const ConfigEditor = ({ guildId }) => {
  const { data, loading, error, refetch } = useApi(() => getTicketConfig(guildId));
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data?.config && form === null) {
      setForm(data.config);
      setSaved(data.config);
    }
  }, [data, form]);

  if (loading) return <SkeletonRows testId="tickets-config-loading" />;
  if (error) return <ErrorNote message={error.message} onRetry={refetch} testId="tickets-config-error" />;
  if (!form) return null;

  const isDirty = JSON.stringify(form) !== JSON.stringify(saved);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    for (const f of ["panel_thumbnail", "panel_image", "ticket_thumbnail", "ticket_image"]) {
      if (validateUrl(form[f])) {
        toast.error(`${f.replace(/_/g, " ")} is invalid.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {};
      ["panel_title", "panel_description", "ticket_title", "ticket_description", "color", "panel_thumbnail", "panel_image", "panel_footer", "ticket_thumbnail", "ticket_image", "ticket_footer", "max_open", "transcripts"].forEach((k) => {
        if (form[k] !== undefined) payload[k] = form[k] === "" ? null : form[k];
      });
      const res = await updateTicketConfig(guildId, payload);
      setForm(res.config);
      setSaved(res.config);
      toast.success("Ticket configuration saved.");
    } catch (e) {
      showApiError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="tickets-config-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Panel title">
          <TextInput value={form.panel_title} onChange={(v) => set({ panel_title: v })} placeholder="Support Center" testId="tickets-panel-title-cfg" />
        </Field>
        <Field label="Ticket title">
          <TextInput value={form.ticket_title} onChange={(v) => set({ ticket_title: v })} placeholder="Ticket opened" testId="tickets-ticket-title-cfg" />
        </Field>
      </div>
      <Field label="Panel description" hint="Line breaks are preserved in the embed.">
        <Textarea value={form.panel_description} onChange={(v) => set({ panel_description: v })} placeholder={"Choose a category below to open a ticket.\nOur team responds as fast as possible."} testId="tickets-panel-description-cfg" />
      </Field>
      <Field label="Ticket description" hint="Shown inside each new ticket. Line breaks are preserved.">
        <Textarea value={form.ticket_description} onChange={(v) => set({ ticket_description: v })} placeholder="Describe your issue…" testId="tickets-ticket-description-cfg" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Embed color">
          <ColorInput value={form.color} onChange={(v) => set({ color: v })} testId="tickets-color-cfg" />
        </Field>
        <Field label="Max open tickets per member">
          <NumInput value={form.max_open} onChange={(v) => set({ max_open: v })} min={1} testId="tickets-max-open-cfg" />
        </Field>
        <Field label="Panel footer">
          <TextInput value={form.panel_footer} onChange={(v) => set({ panel_footer: v })} testId="tickets-panel-footer-cfg" />
        </Field>
        <Field label="Ticket footer">
          <TextInput value={form.ticket_footer} onChange={(v) => set({ ticket_footer: v })} testId="tickets-ticket-footer-cfg" />
        </Field>
        <Field label="Panel thumbnail URL">
          <TextInput value={form.panel_thumbnail} onChange={(v) => set({ panel_thumbnail: v })} placeholder="https://…" testId="tickets-panel-thumbnail-cfg" />
        </Field>
        <Field label="Panel image URL">
          <TextInput value={form.panel_image} onChange={(v) => set({ panel_image: v })} placeholder="https://…" testId="tickets-panel-image-cfg" />
        </Field>
        <Field label="Ticket thumbnail URL">
          <TextInput value={form.ticket_thumbnail} onChange={(v) => set({ ticket_thumbnail: v })} placeholder="https://…" testId="tickets-ticket-thumbnail-cfg" />
        </Field>
        <Field label="Ticket image URL">
          <TextInput value={form.ticket_image} onChange={(v) => set({ ticket_image: v })} placeholder="https://…" testId="tickets-ticket-image-cfg" />
        </Field>
      </div>
      <ToggleRow label="Save transcripts" checked={!!form.transcripts} onChange={(v) => set({ transcripts: v })} testId="tickets-transcripts-cfg" />
      <div className="border-t border-white/5 pt-4">
        <button
          onClick={save}
          disabled={!isDirty || saving}
          data-testid="tickets-config-save"
          className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
            isDirty && !saving ? "bg-white text-black hover:scale-[1.02]" : "cursor-not-allowed border border-white/10 text-paper-500"
          }`}
        >
          {saving ? "Saving…" : isDirty ? "Save configuration" : "Saved"}
        </button>
      </div>
    </div>
  );
};

export const TicketsManager = ({ guildId, channels, roles }) => (
  <GlassCard className="p-6 md:p-8" hover={false} data-testid="module-tickets">
    <div id="tickets" className="scroll-mt-40">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
          <Ticket className="h-5 w-5 text-paper-100" aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-display text-lg font-semibold text-paper-100">Tickets</h3>
          <p className="text-sm text-paper-500">Panels, options, categories, live ticket management, and transcripts.</p>
        </div>
      </div>
      <div className="mt-6 space-y-4">
        <OverviewStrip guildId={guildId} />
        <PanelSection guildId={guildId} channels={channels} />
        <OptionsSection guildId={guildId} roles={roles} />
        <LogsSection guildId={guildId} channels={channels} />
        <ConfigEditor guildId={guildId} />
        <TicketsList guildId={guildId} channels={channels} />
      </div>
    </div>
  </GlassCard>
);
